/**
 * AB#21697 — the customer's in-hand date, as the overview reads it.
 *
 * The backend is the authority for ELSA (DataExpectations computes the same
 * facts with the configured transit allowance); this is the display-side
 * mirror so the column can colour a row without a round trip. Same rules:
 *
 *   'late'  — the promised ship date is on or after the in-hand date: it
 *             cannot arrive in time as planned.
 *   'tight' — it ships before, but with fewer than `transitDays` to spare.
 *   'ok'    — comfortable, or nothing to compare (no promise yet).
 *   null    — no in-hand date.
 */
export type InHandTone = 'late' | 'tight' | 'ok' | null;

export const DEFAULT_IN_HAND_TRANSIT_DAYS = 5;

const day = (value: unknown): Date | null => {
  if (!value) return null;
  const raw = Array.isArray(value) ? value[value.length - 1] : value;
  if (!raw) return null;
  const d = new Date(String(raw).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Whole days from the promised ship date to the in-hand date; null when either is missing. */
export const daysShipToInHand = (inHand: unknown, promised: unknown): number | null => {
  const a = day(promised);
  const b = day(inHand);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
};

export const inHandTone = (
  inHand: unknown,
  promised: unknown,
  transitDays = DEFAULT_IN_HAND_TRANSIT_DAYS
): InHandTone => {
  if (!day(inHand)) return null;
  const gap = daysShipToInHand(inHand, promised);
  if (gap === null) return 'ok';
  if (gap <= 0) return 'late';
  if (gap < transitDays) return 'tight';
  return 'ok';
};

export const inHandTitle = (inHand: unknown, promised: unknown, full: string): string => {
  const gap = daysShipToInHand(inHand, promised);
  if (gap === null) return `In hand ${full} — the date the customer needs the goods`;
  if (gap <= 0)
    return `In hand ${full} — promised ship date is ${-gap === 0 ? 'the same day' : `${-gap} day${-gap === 1 ? '' : 's'} after`}: cannot arrive in time as planned`;
  if (gap < DEFAULT_IN_HAND_TRANSIT_DAYS)
    return `In hand ${full} — only ${gap} day${gap === 1 ? '' : 's'} after the promised ship date`;
  return `In hand ${full} — ${gap} days after the promised ship date`;
};

/** Local calendar day as Y-m-d — never toISOString(), which shifts the day west of UTC. */
export const toYmd = (d: Date | string | null): string | null => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${x.getFullYear()}-${m}-${day}`;
};
